import React, { useState } from 'react';
import { X, ArrowRightLeft, CheckCircle2 } from 'lucide-react';
import { Button } from './Button';
interface ReplaceExerciseSheetProps {
  currentExercise: string;
  onConfirm: (newExercise: string) => void;
  onClose: () => void;
}
export function ReplaceExerciseSheet({
  currentExercise,
  onConfirm,
  onClose
}: ReplaceExerciseSheetProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const movementFamily = 'Compound Quad-Dominant';
  const alternatives = [{
    name: 'Dumbbell Goblet Squat',
    equipment: 'Dumbbell',
    focus: 'Quads',
    match: 'Same movement pattern'
  }, {
    name: 'Leg Press',
    equipment: 'Machine',
    focus: 'Quads',
    match: 'Same primary muscle'
  }, {
    name: 'Bulgarian Split Squat',
    equipment: 'Dumbbell',
    focus: 'Quads / Glutes',
    match: 'Similar load profile'
  }, {
    name: 'Front Squat',
    equipment: 'Barbell',
    focus: 'Quads / Core',
    match: 'Same equipment class'
  }];
  return <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end animate-in fade-in duration-200" data-id="element-341">
      <div className="bg-white w-full rounded-t-[2.5rem] p-6 pb-8 shadow-2xl animate-in slide-in-from-bottom-full duration-300" data-id="element-342">
        <div className="flex items-center justify-between mb-6" data-id="element-343">
          <div className="flex items-center gap-3" data-id="element-344">
            <div className="w-10 h-10 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center" data-id="element-345">
              <ArrowRightLeft size={20} data-id="element-346" />
            </div>
            <div data-id="element-347">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-348">
                Replacing
              </p>
              <h2 className="text-lg font-extrabold text-slate-800" data-id="element-349">
                {currentExercise}
              </h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-350">
            <X size={24} data-id="element-351" />
          </button>
        </div>

        <p className="text-xs font-medium text-slate-400 mb-4 ml-1" data-id="element-352">
          Showing compatible replacements from the{' '}
          <span className="font-bold text-slate-500" data-id="element-353">{movementFamily}</span>{' '}
          family.
        </p>

        <div className="space-y-3 mb-6 max-h-[40vh] overflow-y-auto pr-2" data-id="element-354">
          {alternatives.map(alt => {
          const isSelected = selected === alt.name;
          return <button key={alt.name} onClick={() => setSelected(alt.name)} className={`
                  w-full text-left p-4 rounded-2xl transition-all duration-200 border-2 flex items-center justify-between
                  ${isSelected ? 'bg-blue-50/50 border-blue-500 shadow-sm' : 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50'}
                `} data-id="element-355">
                <div data-id="element-356">
                  <h3 className={`font-extrabold mb-1.5 ${isSelected ? 'text-blue-700' : 'text-slate-800'}`} data-id="element-357">
                    {alt.name}
                  </h3>
                  <div className="flex flex-wrap gap-2 text-xs font-bold" data-id="element-358">
                    <span className={`px-2 py-0.5 rounded-md ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-359">
                      {alt.equipment}
                    </span>
                    <span className={`px-2 py-0.5 rounded-md ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-360">
                      {alt.focus}
                    </span>
                    <span className={`px-2 py-0.5 rounded-md ${isSelected ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-50 text-emerald-500'}`} data-id="element-361">
                      {alt.match}
                    </span>
                  </div>
                </div>
                {isSelected && <CheckCircle2 className="text-blue-500 shrink-0 ml-3" size={20} data-id="element-362" />}
              </button>;
        })}
        </div>

        <Button variant="primary" fullWidth disabled={!selected} onClick={() => selected && onConfirm(selected)} className={selected ? '!bg-blue-500 hover:!bg-blue-600 focus:!ring-blue-500' : ''} data-id="element-363">
          Confirm replacement
        </Button>
      </div>
    </div>;
}